const post = {
  slug: 'evergreen-vs-trending-youtube',
  cluster: 'topics',
  eyebrow: 'Topics',
  title: 'Evergreen or trending? What each actually costs',
  description:
    'The two produce completely different channels. Which one suits a faceless channel, and the mixed strategy that mostly does not work.',
  keywords: [
    'evergreen content youtube', 'trending videos youtube', 'evergreen vs trending',
    'youtube content strategy', 'long tail youtube', 'youtube content planning',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/evergreen-vs-trending-youtube.webp',
    alt: 'Two view-count curves: a tall narrow spike beside a low line that continues indefinitely.',
  },
  lede:
    'These are not two flavours of the same strategy. They produce different channels, reward different work, and fail in different ways — and one of them fits a faceless channel considerably better.',
  sections: [
    {
      heading: 'The shape of each',
      body: [
        {
          type: 'para',
          text: 'A trending video has a tall, narrow curve: most of its lifetime views in the first days, then near zero. An evergreen video has a low, long one: modest early numbers that continue, sometimes for years, and often *increase* as the channel gains standing.',
        },
        {
          type: 'para',
          text: 'The consequence people miss: an evergreen library is cumulative. Video twenty is still earning while you make video twenty-one, so a channel of thirty evergreen videos has thirty things working. A channel of thirty trending videos has one.',
        },
      ],
    },
    {
      heading: 'Why trending is hard for a faceless channel',
      body: [
        {
          type: 'para',
          text: 'Three reasons, all structural rather than about quality.',
        },
        {
          type: 'list',
          ordered: true,
          items: [
            '**Speed.** Trending rewards being early. A research-heavy scripted video takes a day or more; by then the window is often closing.',
            '**Research depth is wasted.** The advantage of a careful faceless video is depth, and a trending audience is not there for depth — they want the fast take.',
            '**The audience does not transfer.** Subscribers gained from a trend spike came for the trend. They will not watch your next video, which makes your subsequent numbers look worse, not better.',
          ],
        },
        {
          type: 'para',
          text: 'That third one is genuinely counterproductive: a viral video outside your usual material can leave a channel measurably harder to grow than before it.',
        },
      ],
    },
    {
      heading: 'Why evergreen suits it',
      body: [
        {
          type: 'para',
          text: 'The strengths line up exactly. Depth pays off because the video has years to find its audience. Slow production is fine because there is no window. And search demand for a well-chosen evergreen question does not decay.',
        },
        {
          type: 'para',
          text: 'There is also a compounding effect that is easy to underrate: the research you do for one evergreen video in a narrow niche makes the next one faster. See [why narrow wins](/blogs/narrow-youtube-niche).',
        },
        {
          type: 'quote',
          text: 'A trending video is a spike you have to keep re-earning. An evergreen library is an asset that keeps paying while you sleep.',
        },
      ],
    },
    {
      heading: 'The mixed strategy that does not work',
      body: [
        {
          type: 'para',
          text: 'The common plan: mostly evergreen, with occasional trending videos "for reach". It usually underperforms both.',
        },
        {
          type: 'para',
          text: 'The trending videos are made by someone without the speed or format to compete on trends, so they do modestly. And they dilute what the channel is, which weakens the thing that made the evergreen videos work.',
        },
        {
          type: 'para',
          text: 'The version that does work is narrower: cover a current event **only when it is a specific instance of your existing subject**. Then it is an evergreen video with a timely hook — it spikes now and keeps earning later, and it reinforces rather than dilutes the channel.',
        },
      ],
    },
    {
      heading: 'How to tell which one a topic is',
      body: [
        {
          type: 'para',
          text: 'Ask whether someone would search this in two years. "Why did this company collapse" — yes, indefinitely. "Reaction to this week\'s announcement" — no, and not in two months either.',
        },
        {
          type: 'para',
          text: 'Borderline cases are usually evergreen topics with a dated framing, and they are worth rewriting: "What the new rules mean" ages out, while the underlying mechanism it explains does not.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How long before an evergreen video finds its audience?',
      a: 'Often much longer than feels reasonable — months is common, and videos picking up a year later is not unusual. This is why judging an evergreen video by its first-week numbers is misleading.',
    },
    {
      q: 'Should I update old evergreen videos?',
      a: 'You cannot edit an uploaded video, but you can update the title, thumbnail and description, and those are worth revisiting on anything with good retention and poor click-through. For genuinely outdated content, a new version usually beats leaving a wrong one up.',
    },
  ],
  related: ['youtube-topic-research', 'youtube-search-vs-browse-traffic', 'narrow-youtube-niche'],
};

export default post;
